"use client";

import { useRouter } from "next/navigation";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocalStorage } from "usehooks-ts";

import { DIALOG, setDialog } from "@/components/app/dialogs/dialog-provider";
import { DiscoverFilterFields } from "@/components/app/discover-sort-constants";
import { LaunchpadDiscoverProvider } from "@/components/app/trade/launchpad/launchpad";
import { UpcomingTokensTab } from "@/components/app/trade/launchpad/upcoming-tokens-tab/upcoming-tokens-tab";
import { EmptyState } from "@/components/shared/empty-state";
import { useIsLoggedIn } from "@/components/shared/is-logged-in";
import { createToastErrorDetails } from "@/components/ui/toast-helpers";
import { toast } from "@/components/ui/use-toast";
import {
  FeedProviderInternal,
  type PostType,
  useFeedContext,
} from "@/contexts/feed-context";
import { type BasicSort, SortType } from "@/contexts/filters-sort-context";
import { UserContext } from "@/contexts/user-context";
import { PostsOrderBy } from "@/graphql/codegen/graphql";
import { useFocusPreferences } from "@/hooks/use-focus-preferences";
import { useIsClient } from "@/hooks/use-is-client";
import { usePostEditor } from "@/hooks/use-post-editor";
import { useGetPosts } from "@/hooks-api/use-get-posts";
import { APP_ROUTES, FOCUS_LAUNCH_DATE, POST_LIMIT } from "@/utils/constants";
import { feedFreezeDebugLog } from "@/utils/feed-freeze-debug";
import { isFeedQueryPresetId } from "@/utils/feed-query-presets";
import {
  areFeedSurfaceRefsEqual,
  createBuiltinFeedRef,
  type FeedSurfaceRef,
  getFeedSurfaceKey,
  parseFeedSurfaceRef,
  serializeFeedSurfaceRef,
} from "@/utils/feed-surface";
import {
  resolveFeedSurfaceItem,
  resolveFeedSurfaceItems,
  resolveFeedSurfaceOrder,
} from "@/utils/focus-preferences";
import { isAbortError } from "@/utils/helpers";
import { LocalStorageKeys } from "@/utils/local-storage-keys";

import { Feed } from "../../feed";
import type { PendingPost } from "../../feed-pending-post-item";
import { FeedPendingPostItem } from "../../feed-pending-post-item";
import { FeedPostSkeletonList } from "../feed-post-skeleton-list";
import { FeedMainHeader, FeedMainHeaderSkeleton } from "./feed-main-header";
import { FeedSelectorRow, FeedSelectorRowSkeleton } from "./feed-selector-row";

export const FeedMainWrapper = () => {
  const router = useRouter();
  const [initialLoading, setInitialLoading] = useState(true);
  const [pendingPosts, setPendingPosts] = useState<PendingPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [resolvedEmptySurfaceStateKey, setResolvedEmptySurfaceStateKey] =
    useState<string | null>(null);
  const { showEditor: showHeader } = usePostEditor();
  const { preferences } = useFocusPreferences();
  const {
    currentUserPublicKey,
    toggleBlockUser,
    userLists,
    userListsReady,
    userLoading,
  } = useContext(UserContext);
  const feedLogic = useFeedContext();
  const { clearScrollPosition, setPosts, setAdminPinnedPosts } = feedLogic;
  const isClient = useIsClient();
  const isLoggedIn = useIsLoggedIn();
  const [storedActiveSurface, setStoredActiveSurface] = useLocalStorage(
    LocalStorageKeys.mainFeedActiveSurface,
    serializeFeedSurfaceRef(createBuiltinFeedRef("for-you")),
    {
      initializeWithValue: false,
    },
  );
  const [
    storedTemporarySurface,
    setStoredTemporarySurface,
    removeStoredTemporarySurface,
  ] = useLocalStorage(LocalStorageKeys.mainFeedTemporarySurface, "", {
    initializeWithValue: false,
  });
  const [surfaceStorageHydrated, setSurfaceStorageHydrated] = useState(false);

  const initialRequestIdRef = useRef(0);
  const bootstrapInFlightSurfaceStateKeyRef = useRef<string | null>(null);
  const initialTempFiltersRef = useRef(JSON.stringify(feedLogic.tempFilters));
  const previousActiveSurfaceStateKeyRef = useRef<string | null>(null);
  const latestViewerPublicKeyRef = useRef(currentUserPublicKey);
  const [userSourcesReady, setUserSourcesReady] = useState(() => !isLoggedIn);
  latestViewerPublicKeyRef.current = currentUserPublicKey;

  const {
    getPostsForYouFeed,
    getPostsFromFollowingPeople,
    getPostsFromSubscribers,
    getPostsFromList,
    getPostsFromCustomFeed,
    getPostsFromPreset,
    getAdminPinnedPosts,
  } = useGetPosts();

  const { initializePosts } = feedLogic;

  const resolvedSurfaceItems = useMemo(() => {
    return resolveFeedSurfaceItems(preferences, {
      loggedIn: isLoggedIn,
      lists: userLists,
    });
  }, [isLoggedIn, preferences, userLists]);
  const configuredSurfaceOrder = useMemo(() => {
    return resolveFeedSurfaceOrder(preferences, {
      loggedIn: isLoggedIn,
      availableCustomFeedIds: Object.keys(preferences.customFeeds),
    });
  }, [isLoggedIn, preferences]);
  const storedActiveSurfaceRef = useMemo(() => {
    return parseFeedSurfaceRef(storedActiveSurface);
  }, [storedActiveSurface]);
  const temporarySurface = useMemo(() => {
    return parseFeedSurfaceRef(storedTemporarySurface);
  }, [storedTemporarySurface]);
  const resolvedTemporarySurface = useMemo(() => {
    if (!temporarySurface) {
      return null;
    }

    return resolveFeedSurfaceItem(temporarySurface, preferences, {
      loggedIn: isLoggedIn,
      lists: userLists,
    });
  }, [isLoggedIn, preferences, temporarySurface, userLists]);
  const temporarySurfaceAlreadyVisible = useMemo(() => {
    if (!temporarySurface) {
      return false;
    }

    return resolvedSurfaceItems.some((surface) =>
      areFeedSurfaceRefsEqual(surface.ref, temporarySurface),
    );
  }, [resolvedSurfaceItems, temporarySurface]);
  const temporarySurfaceSourcesReady = useMemo(() => {
    if (!temporarySurface) {
      return true;
    }

    if (temporarySurface.type === "builtin") {
      return true;
    }

    if (temporarySurface.type === "list") {
      return userListsReady;
    }

    return userSourcesReady;
  }, [temporarySurface, userListsReady, userSourcesReady]);
  const activeSurfaceSourcesReady = useMemo(() => {
    if (!storedActiveSurfaceRef) {
      return true;
    }

    if (storedActiveSurfaceRef.type === "list") {
      return userListsReady;
    }

    if (storedActiveSurfaceRef.type === "custom-feed") {
      return userSourcesReady;
    }

    return true;
  }, [storedActiveSurfaceRef, userListsReady, userSourcesReady]);
  const configuredSurfaceSourcesReady = useMemo(() => {
    if (isLoggedIn && !userSourcesReady) {
      return false;
    }

    const requiresLists = configuredSurfaceOrder.some(
      (surface) => surface.type === "list",
    );

    if (requiresLists && !userListsReady) {
      return false;
    }

    return true;
  }, [configuredSurfaceOrder, isLoggedIn, userListsReady, userSourcesReady]);
  const surfaceItems = useMemo(() => {
    if (!resolvedTemporarySurface || temporarySurfaceAlreadyVisible) {
      return resolvedSurfaceItems;
    }

    return [...resolvedSurfaceItems, resolvedTemporarySurface];
  }, [
    resolvedSurfaceItems,
    resolvedTemporarySurface,
    temporarySurfaceAlreadyVisible,
  ]);

  const fallbackSurface =
    resolvedSurfaceItems[0]?.ref ?? createBuiltinFeedRef("for-you");
  const fallbackSurfaceKey = getFeedSurfaceKey(fallbackSurface);
  const activeSurface = storedActiveSurfaceRef ?? fallbackSurface;
  const activeSurfaceItem = useMemo(() => {
    if (!surfaceStorageHydrated || !activeSurfaceSourcesReady) {
      return null;
    }

    return (
      surfaceItems.find((surface) =>
        areFeedSurfaceRefsEqual(surface.ref, activeSurface),
      ) ??
      resolveFeedSurfaceItem(activeSurface, preferences, {
        loggedIn: isLoggedIn,
        lists: userLists,
      }) ??
      surfaceItems[0] ??
      null
    );
  }, [
    activeSurface,
    activeSurfaceSourcesReady,
    isLoggedIn,
    preferences,
    surfaceItems,
    surfaceStorageHydrated,
    userLists,
  ]);
  const getSurfaceStateKey = useCallback(
    (surfaceItem: typeof activeSurfaceItem) => {
      if (!surfaceItem) {
        return null;
      }

      if (surfaceItem.ref.type === "list") {
        return [
          surfaceItem.key,
          surfaceItem.list?.UpdatedAt ?? "",
          surfaceItem.list?.MembersCount ?? 0,
        ].join(":");
      }

      if (surfaceItem.ref.type === "custom-feed") {
        return [surfaceItem.key, surfaceItem.customFeed?.updatedAt ?? ""].join(
          ":",
        );
      }

      return surfaceItem.key;
    },
    [],
  );
  const activeSurfaceStateKey = useMemo(() => {
    const surfaceStateKey =
      getSurfaceStateKey(activeSurfaceItem) ?? fallbackSurfaceKey;
    const viewerStateKey =
      currentUserPublicKey || (isLoggedIn ? "pending-user" : "guest");

    return `${viewerStateKey}:${surfaceStateKey}`;
  }, [
    activeSurfaceItem,
    currentUserPublicKey,
    fallbackSurfaceKey,
    getSurfaceStateKey,
    isLoggedIn,
  ]);
  const getSurfaceScrollRestorationKey = useCallback(
    (surfaceItem: typeof activeSurfaceItem, surface: FeedSurfaceRef) => {
      const surfaceStateKey =
        getSurfaceStateKey(surfaceItem) ?? getFeedSurfaceKey(surface);
      const viewerStateKey =
        currentUserPublicKey || (isLoggedIn ? "pending-user" : "guest");

      return `main-feed:${viewerStateKey}:${surfaceStateKey}`;
    },
    [currentUserPublicKey, getSurfaceStateKey, isLoggedIn],
  );
  const activeScrollRestorationKey = useMemo(
    () => `main-feed:${activeSurfaceStateKey}`,
    [activeSurfaceStateKey],
  );
  const feedSurfaceReady =
    isClient &&
    surfaceStorageHydrated &&
    configuredSurfaceSourcesReady &&
    activeSurfaceSourcesReady &&
    (!!activeSurfaceItem || surfaceItems.length === 0);

  const activeTempFiltersKey = JSON.stringify(feedLogic.tempFilters);

  const resetFeedState = useCallback(
    (options?: { scrollRestorationKey?: string }) => {
      feedFreezeDebugLog("main-feed", "resetFeedState", {
        activeScrollRestorationKey,
        adminPinnedPostsLength: feedLogic.adminPinnedPosts.length,
        nextInitialRequestId: initialRequestIdRef.current + 1,
        postsLength: feedLogic.posts.length,
        scrollRestorationKey:
          options?.scrollRestorationKey ?? activeScrollRestorationKey,
      });

      initialRequestIdRef.current += 1;
      bootstrapInFlightSurfaceStateKeyRef.current = null;
      clearScrollPosition(
        options?.scrollRestorationKey ?? activeScrollRestorationKey,
      );
      window.scrollTo(0, 0);
      setResolvedEmptySurfaceStateKey(null);
      setInitialLoading(true);
      setLoading(true);
      setPage(0);
      setAdminPinnedPosts([]);
      setPosts([]);
    },
    [
      activeScrollRestorationKey,
      clearScrollPosition,
      feedLogic.adminPinnedPosts.length,
      feedLogic.posts.length,
      setAdminPinnedPosts,
      setPosts,
    ],
  );

  const resolveSurfaceItemForRef = useCallback(
    (surface: FeedSurfaceRef) => {
      return (
        surfaceItems.find((surfaceItem) =>
          areFeedSurfaceRefsEqual(surfaceItem.ref, surface),
        ) ??
        resolveFeedSurfaceItem(surface, preferences, {
          loggedIn: isLoggedIn,
          lists: userLists,
        }) ??
        null
      );
    },
    [isLoggedIn, preferences, surfaceItems, userLists],
  );

  const setActiveSurface = useCallback(
    (nextSurface: FeedSurfaceRef) => {
      setStoredActiveSurface(serializeFeedSurfaceRef(nextSurface));
    },
    [setStoredActiveSurface],
  );
  const selectSurface = useCallback(
    (
      nextSurface: FeedSurfaceRef,
      options?: {
        temporary?: boolean;
      },
    ) => {
      if (options?.temporary) {
        setStoredTemporarySurface(serializeFeedSurfaceRef(nextSurface));
      } else {
        removeStoredTemporarySurface();
      }

      const nextSurfaceItem = resolveSurfaceItemForRef(nextSurface);
      const nextSurfaceBaseStateKey =
        getSurfaceStateKey(nextSurfaceItem) ?? getFeedSurfaceKey(nextSurface);
      const viewerStateKey =
        currentUserPublicKey || (isLoggedIn ? "pending-user" : "guest");
      const nextSurfaceStateKey = `${viewerStateKey}:${nextSurfaceBaseStateKey}`;
      const scrollRestorationKey = getSurfaceScrollRestorationKey(
        nextSurfaceItem,
        nextSurface,
      );

      feedFreezeDebugLog("main-feed", "selectSurface", {
        activeSurface: getFeedSurfaceKey(activeSurface),
        activeSurfaceStateKey,
        nextSurface: getFeedSurfaceKey(nextSurface),
        nextSurfaceItemLabel: nextSurfaceItem?.label ?? null,
        nextSurfaceStateKey,
        scrollRestorationKey,
        temporary: !!options?.temporary,
      });

      previousActiveSurfaceStateKeyRef.current = nextSurfaceStateKey;
      resetFeedState({
        scrollRestorationKey,
      });
      setActiveSurface(nextSurface);
    },
    [
      activeSurface,
      activeSurfaceStateKey,
      currentUserPublicKey,
      getSurfaceScrollRestorationKey,
      getSurfaceStateKey,
      isLoggedIn,
      removeStoredTemporarySurface,
      resetFeedState,
      resolveSurfaceItemForRef,
      setActiveSurface,
      setStoredTemporarySurface,
    ],
  );

  useEffect(() => {
    if (!isClient) {
      return;
    }

    setSurfaceStorageHydrated(true);
  }, [isClient, storedActiveSurface, storedTemporarySurface]);

  useEffect(() => {
    if (!isLoggedIn || !currentUserPublicKey) {
      setUserSourcesReady(true);
      return;
    }

    setUserSourcesReady(false);
  }, [currentUserPublicKey, isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn || !currentUserPublicKey || !userLoading) {
      setUserSourcesReady(true);
    }
  }, [currentUserPublicKey, isLoggedIn, userLoading]);

  useEffect(() => {
    if (!surfaceStorageHydrated) {
      return;
    }

    if (!temporarySurface) {
      return;
    }

    if (temporarySurfaceAlreadyVisible) {
      removeStoredTemporarySurface();
      return;
    }

    if (!temporarySurfaceSourcesReady) {
      return;
    }

    if (!resolvedTemporarySurface) {
      if (areFeedSurfaceRefsEqual(activeSurface, temporarySurface)) {
        setActiveSurface(fallbackSurface);
      }

      removeStoredTemporarySurface();
    }
  }, [
    activeSurface,
    fallbackSurface,
    removeStoredTemporarySurface,
    resolvedTemporarySurface,
    temporarySurface,
    temporarySurfaceAlreadyVisible,
    temporarySurfaceSourcesReady,
    surfaceStorageHydrated,
    setActiveSurface,
  ]);

  useEffect(() => {
    if (!surfaceStorageHydrated || !activeSurfaceSourcesReady) {
      return;
    }

    if (activeSurfaceItem) {
      return;
    }

    feedFreezeDebugLog("main-feed", "fallbackActiveSurface", {
      activeSurface: getFeedSurfaceKey(activeSurface),
      activeSurfaceSourcesReady,
      fallbackSurface: getFeedSurfaceKey(fallbackSurface),
      storedActiveSurface,
      surfaceStorageHydrated,
    });

    setActiveSurface(fallbackSurface);
  }, [
    activeSurface,
    activeSurfaceItem,
    activeSurfaceSourcesReady,
    fallbackSurface,
    setActiveSurface,
    storedActiveSurface,
    surfaceStorageHydrated,
  ]);

  useEffect(() => {
    if (!feedSurfaceReady || !activeSurfaceItem) {
      return;
    }

    const nextActiveSurfaceKey = activeSurfaceStateKey ?? fallbackSurfaceKey;

    if (previousActiveSurfaceStateKeyRef.current === null) {
      previousActiveSurfaceStateKeyRef.current = nextActiveSurfaceKey;
      feedFreezeDebugLog("main-feed", "surfaceStateKey:init", {
        activeSurfaceStateKey,
        fallbackSurfaceKey,
        nextActiveSurfaceKey,
      });
      return;
    }

    if (previousActiveSurfaceStateKeyRef.current === nextActiveSurfaceKey) {
      return;
    }

    feedFreezeDebugLog("main-feed", "surfaceStateKey:changed", {
      activeSurfaceStateKey,
      fallbackSurfaceKey,
      nextActiveSurfaceKey,
      previousActiveSurfaceStateKey: previousActiveSurfaceStateKeyRef.current,
    });

    previousActiveSurfaceStateKeyRef.current = nextActiveSurfaceKey;
    resetFeedState();
  }, [
    activeSurfaceStateKey,
    fallbackSurfaceKey,
    feedSurfaceReady,
    resetFeedState,
  ]);

  const getPostsForYouRequest = useCallback(async () => {
    const nextPage = page + 1;
    const data = await getPostsForYouFeed({
      limit: POST_LIMIT,
      page: nextPage,
    });

    setPage(nextPage);
    return data;
  }, [getPostsForYouFeed, page]);

  const getActiveSurfacePostsMethod = useMemo(() => {
    if (!activeSurfaceItem) {
      return async () => ({ posts: [] });
    }

    if (activeSurfaceItem.ref.type === "list") {
      return async ({
        filters,
        orderBy,
        offset,
        context,
      }: Parameters<typeof getPostsFromList>[0] = {}) =>
        getPostsFromList({
          filters,
          orderBy,
          offset,
          context,
          listId: activeSurfaceItem.list?.ListId,
          listName: activeSurfaceItem.list?.ListName,
        });
    }

    if (activeSurfaceItem.ref.type === "custom-feed") {
      const customFeed = activeSurfaceItem.customFeed;

      if (!customFeed) {
        return async () => ({ posts: [] });
      }

      return (params = {}) => getPostsFromCustomFeed(customFeed, params);
    }

    switch (activeSurfaceItem.ref.id) {
      case "for-you":
        return getPostsForYouRequest;
      case "following":
        return getPostsFromFollowingPeople;
      case "subscriptions":
        return getPostsFromSubscribers;
      default:
        if (isFeedQueryPresetId(activeSurfaceItem.ref.id)) {
          const presetId = activeSurfaceItem.ref.id;
          return (params = {}) => getPostsFromPreset(presetId, params);
        }

        return async () => ({ posts: [] });
    }
  }, [
    activeSurfaceItem,
    getPostsForYouRequest,
    getPostsFromFollowingPeople,
    getPostsFromList,
    getPostsFromPreset,
    getPostsFromSubscribers,
  ]);

  const initialRequest = useCallback(async () => {
    const requestId = ++initialRequestIdRef.current;
    const requestViewerPublicKey = currentUserPublicKey;
    const requestSurfaceStateKey = activeSurfaceStateKey ?? fallbackSurfaceKey;
    const isStaleInitialRequest = () =>
      requestId !== initialRequestIdRef.current ||
      requestViewerPublicKey !== latestViewerPublicKeyRef.current;

    bootstrapInFlightSurfaceStateKeyRef.current = requestSurfaceStateKey;

    feedFreezeDebugLog("main-feed", "initialRequest:start", {
      activeSurface: activeSurfaceItem
        ? getFeedSurfaceKey(activeSurfaceItem.ref)
        : null,
      postsLength: feedLogic.posts.length,
      requestId,
      requestSurfaceStateKey,
    });

    if (!activeSurfaceItem) {
      if (!isStaleInitialRequest()) {
        setInitialLoading(false);
        setLoading(false);
      }
      return;
    }

    if (
      activeSurfaceItem.ref.type === "builtin" &&
      activeSurfaceItem.ref.id === "launchpad"
    ) {
      if (!isStaleInitialRequest()) {
        setInitialLoading(false);
        setLoading(false);
      }
      return;
    }

    let posts: PostType[] = [];
    let hasRenderableResults = false;
    setLoading(true);
    setResolvedEmptySurfaceStateKey(null);

    try {
      if (activeSurfaceItem.ref.type === "list") {
        ({ posts } = await getPostsFromList({
          listId: activeSurfaceItem.list?.ListId,
          listName: activeSurfaceItem.list?.ListName,
        }));
      } else if (activeSurfaceItem.ref.type === "custom-feed") {
        const customFeed = activeSurfaceItem.customFeed;

        if (customFeed) {
          ({ posts } = await getPostsFromCustomFeed(customFeed));
        }
      } else {
        switch (activeSurfaceItem.ref.id) {
          case "following":
            ({ posts } = await getPostsFromFollowingPeople());
            break;
          case "subscriptions":
            ({ posts } = await getPostsFromSubscribers());
            break;
          case "for-you": {
            const { posts: pinnedPosts } = await getAdminPinnedPosts({
              orderBy: PostsOrderBy.TimestampDesc,
              offset: 0,
            });

            if (isStaleInitialRequest()) {
              feedFreezeDebugLog("main-feed", "initialRequest:stalePinned", {
                requestId,
                requestSurfaceStateKey,
              });
              return;
            }

            setAdminPinnedPosts(pinnedPosts);
            hasRenderableResults = pinnedPosts.length > 0;
            const data = await getPostsForYouFeed({
              limit: POST_LIMIT,
              page: 0,
            });

            if (isStaleInitialRequest()) {
              feedFreezeDebugLog("main-feed", "initialRequest:staleForYou", {
                requestId,
                requestSurfaceStateKey,
              });
              return;
            }

            posts = data.posts;
            break;
          }
          default:
            if (isFeedQueryPresetId(activeSurfaceItem.ref.id)) {
              const presetId = activeSurfaceItem.ref.id;
              ({ posts } = await getPostsFromPreset(presetId));
            }
        }
      }

      if (isStaleInitialRequest()) {
        feedFreezeDebugLog("main-feed", "initialRequest:staleResult", {
          postCount: posts.length,
          requestId,
          requestSurfaceStateKey,
        });
        return;
      }

      if (posts.length > 0) {
        feedFreezeDebugLog("main-feed", "initialRequest:applyPosts", {
          firstPostHash: posts[0]?.postHash ?? null,
          postCount: posts.length,
          requestId,
          requestSurfaceStateKey,
        });
        hasRenderableResults = true;
        initializePosts(posts);
        return;
      }

      if (!hasRenderableResults) {
        feedFreezeDebugLog("main-feed", "initialRequest:empty", {
          requestId,
          requestSurfaceStateKey,
        });
        setResolvedEmptySurfaceStateKey(requestSurfaceStateKey);
        setInitialLoading(false);
        setLoading(false);
        return;
      }

      setInitialLoading(false);
      setLoading(false);
    } catch (error) {
      if (isStaleInitialRequest()) {
        feedFreezeDebugLog("main-feed", "initialRequest:staleError", {
          requestId,
          requestSurfaceStateKey,
        });
        return;
      }

      setInitialLoading(false);
      setLoading(false);
      throw error;
    } finally {
      if (
        !isStaleInitialRequest() &&
        bootstrapInFlightSurfaceStateKeyRef.current === requestSurfaceStateKey
      ) {
        bootstrapInFlightSurfaceStateKeyRef.current = null;
      }
    }
  }, [
    activeSurfaceItem,
    activeSurfaceStateKey,
    currentUserPublicKey,
    fallbackSurfaceKey,
    feedLogic.posts.length,
    getAdminPinnedPosts,
    getPostsForYouFeed,
    getPostsFromCustomFeed,
    getPostsFromFollowingPeople,
    getPostsFromList,
    getPostsFromPreset,
    getPostsFromSubscribers,
    initializePosts,
    setAdminPinnedPosts,
  ]);

  useEffect(() => {
    if (activeTempFiltersKey === initialTempFiltersRef.current || !isClient) {
      return;
    }

    initialTempFiltersRef.current = activeTempFiltersKey;
    resetFeedState();
  }, [activeTempFiltersKey, isClient, resetFeedState]);

  useEffect(() => {
    if (!activeSurfaceItem) {
      return;
    }

    if (
      activeSurfaceItem.ref.type === "builtin" &&
      activeSurfaceItem.ref.id === "launchpad"
    ) {
      feedFreezeDebugLog("main-feed", "initialRequest:skipLaunchpad", {
        activeSurfaceStateKey,
      });
      setLoading(false);
      setInitialLoading(false);
      return;
    }

    if (
      feedLogic.posts.length > 0 ||
      feedLogic.adminPinnedPosts.length > 0 ||
      resolvedEmptySurfaceStateKey === activeSurfaceStateKey
    ) {
      feedFreezeDebugLog("main-feed", "initialRequest:skipExistingContent", {
        activeSurface: getFeedSurfaceKey(activeSurfaceItem.ref),
        activeSurfaceStateKey,
        adminPinnedPostsLength: feedLogic.adminPinnedPosts.length,
        postsLength: feedLogic.posts.length,
        resolvedEmptySurfaceStateKey,
      });
      setLoading(false);
      setInitialLoading(false);
      return;
    }

    if (
      activeSurfaceStateKey &&
      bootstrapInFlightSurfaceStateKeyRef.current === activeSurfaceStateKey
    ) {
      feedFreezeDebugLog("main-feed", "initialRequest:skipInFlight", {
        activeSurface: getFeedSurfaceKey(activeSurfaceItem.ref),
        activeSurfaceStateKey,
      });
      return;
    }

    void initialRequest().catch((error) => {
      if (isAbortError(error)) {
        return;
      }

      console.error("Failed to initialize homepage feed", error);
      toast({
        variant: "destructive",
        title: "Failed to load feed",
        description: "The feed could not be loaded. Please try again.",
        details: createToastErrorDetails(error),
      });
    });
  }, [
    activeSurfaceItem,
    activeSurfaceStateKey,
    feedLogic.adminPinnedPosts.length,
    feedLogic.posts.length,
    initialRequest,
    resolvedEmptySurfaceStateKey,
  ]);

  const showEmptyState =
    !feedLogic.posts.length &&
    !initialLoading &&
    !loading &&
    resolvedEmptySurfaceStateKey === activeSurfaceStateKey &&
    !feedLogic.adminPinnedPosts.length &&
    !(
      activeSurfaceItem?.ref.type === "builtin" &&
      activeSurfaceItem.ref.id === "launchpad"
    );
  const feedHeaderLoading = !feedSurfaceReady;
  const feedContentLoading = initialLoading || loading || !feedSurfaceReady;

  useEffect(() => {
    feedFreezeDebugLog("main-feed", "renderState", {
      activeScrollRestorationKey,
      activeSurface: getFeedSurfaceKey(activeSurface),
      activeSurfaceItem: activeSurfaceItem
        ? {
            key: activeSurfaceItem.key,
            label: activeSurfaceItem.label,
            ref: getFeedSurfaceKey(activeSurfaceItem.ref),
          }
        : null,
      activeSurfaceSourcesReady,
      activeSurfaceStateKey,
      adminPinnedPostsLength: feedLogic.adminPinnedPosts.length,
      configuredSurfaceSourcesReady,
      feedContentLoading,
      feedSurfaceReady,
      initialLoading,
      isLoggedIn,
      loading,
      postsLength: feedLogic.posts.length,
      resolvedEmptySurfaceStateKey,
      storedActiveSurface,
      storedTemporarySurface,
      surfaceItems: surfaceItems.map((surface) => ({
        key: surface.key,
        label: surface.label,
        ref: getFeedSurfaceKey(surface.ref),
      })),
      surfaceStorageHydrated,
      userListsReady,
      userLoading,
      userSourcesReady,
    });
  }, [
    activeScrollRestorationKey,
    activeSurface,
    activeSurfaceItem,
    activeSurfaceSourcesReady,
    activeSurfaceStateKey,
    configuredSurfaceSourcesReady,
    feedContentLoading,
    feedLogic.adminPinnedPosts.length,
    feedLogic.posts.length,
    feedSurfaceReady,
    initialLoading,
    isLoggedIn,
    loading,
    resolvedEmptySurfaceStateKey,
    storedActiveSurface,
    storedTemporarySurface,
    surfaceItems,
    surfaceStorageHydrated,
    userListsReady,
    userLoading,
    userSourcesReady,
  ]);

  return (
    <FeedProviderInternal {...feedLogic} {...{ onUserBlock: toggleBlockUser }}>
      <div className="relative min-h-safe-screen w-full">
        {feedHeaderLoading ? (
          <FeedMainHeaderSkeleton />
        ) : showHeader && isLoggedIn ? (
          <FeedMainHeader setPendingPosts={setPendingPosts} />
        ) : null}

        {feedSurfaceReady && activeSurfaceItem ? (
          <FeedSelectorRow
            activeSurface={activeSurfaceItem.ref}
            onOpenSettings={() =>
              setDialog(DIALOG.FeedManager, {
                open: true,
              })
            }
            onRefresh={() => {
              resetFeedState();
            }}
            onSelectSurface={(nextSurface) => {
              if (areFeedSurfaceRefsEqual(nextSurface, activeSurfaceItem.ref)) {
                feedFreezeDebugLog("main-feed", "selectSurface:ignoreSame", {
                  activeSurface: getFeedSurfaceKey(activeSurfaceItem.ref),
                });
                return;
              }

              selectSurface(nextSurface, {
                temporary:
                  !!temporarySurface &&
                  areFeedSurfaceRefsEqual(nextSurface, temporarySurface),
              });
            }}
            refreshing={loading}
            showSettings={isLoggedIn}
            surfaces={surfaceItems}
          />
        ) : (
          <FeedSelectorRowSkeleton showSettings={isLoggedIn} />
        )}

        {feedSurfaceReady &&
        activeSurfaceItem?.ref.type === "builtin" &&
        activeSurfaceItem.ref.id === "launchpad" ? (
          <LaunchpadDiscoverProvider
            extraFilters={{
              tokenAmmLaunchAt: {
                isNull: false,
                greaterThan: new Date().toISOString(),
              },
              tokenAmmCreatedAt: {
                greaterThan: FOCUS_LAUNCH_DATE,
              },
            }}
            initialSort={
              {
                type: SortType.basic,
                name: DiscoverFilterFields.tokenAmmLaunchAt,
                direction: "asc",
              } as BasicSort<DiscoverFilterFields>
            }
          >
            <UpcomingTokensTab
              hideFilters
              hideResults
              selectInitial={false}
              clickable={false}
            />
          </LaunchpadDiscoverProvider>
        ) : feedContentLoading ? (
          <FeedPostSkeletonList
            count={10}
            withLeadingBorder={!feedSurfaceReady}
          />
        ) : (
          <>
            {pendingPosts.map((item) => {
              return <FeedPendingPostItem key={item.id} {...item} />;
            })}

            {showEmptyState ? (
              <FeedSurfaceEmptyState
                activeSurface={activeSurfaceItem?.ref ?? fallbackSurface}
                customFeed={activeSurfaceItem?.customFeed}
                listName={activeSurfaceItem?.list?.ListName}
                onManageLists={() =>
                  setDialog(DIALOG.FeedManager, {
                    open: true,
                    params: {
                      section: "lists",
                    },
                  })
                }
                onManageCustomFeed={() => {
                  if (
                    activeSurfaceItem?.ref.type !== "custom-feed" ||
                    !activeSurfaceItem.customFeed
                  ) {
                    return;
                  }

                  setDialog(DIALOG.EditCustomFeed, {
                    open: true,
                    params: {
                      id: activeSurfaceItem.customFeed.id,
                    },
                  });
                }}
                onOpenFeedStore={() => {
                  router.push(APP_ROUTES.feedStore);
                }}
                onOpenDiscover={() => {
                  router.push(APP_ROUTES.discover);
                }}
              />
            ) : (
              <Feed
                showAdminPinnedPosts={
                  activeSurfaceItem?.ref.type === "builtin" &&
                  activeSurfaceItem.ref.id === "for-you"
                }
                getPosts={getActiveSurfacePostsMethod}
                scrollRestorationKey={activeScrollRestorationKey}
              />
            )}
          </>
        )}
      </div>
    </FeedProviderInternal>
  );
};

const FeedSurfaceEmptyState = ({
  activeSurface,
  customFeed,
  listName,
  onManageLists,
  onManageCustomFeed,
  onOpenFeedStore,
  onOpenDiscover,
}: {
  activeSurface: FeedSurfaceRef;
  customFeed?: NonNullable<
    ReturnType<typeof resolveFeedSurfaceItem>
  >["customFeed"];
  listName?: string;
  onManageLists: () => void;
  onManageCustomFeed: () => void;
  onOpenFeedStore: () => void;
  onOpenDiscover: () => void;
}) => {
  const getEmptyStateProps = () => {
    if (activeSurface.type === "list") {
      return {
        image: "/assets/empty-32.png",
        title: `No posts found for ${listName ?? "this list"}.`,
        description:
          "Add or move members in the feed manager to shape this list.",
        buttonText: "Manage Lists",
        onClick: onManageLists,
      };
    }

    if (activeSurface.type === "custom-feed") {
      return {
        image: "/assets/empty-14.png",
        title: `No posts found for ${customFeed?.name ?? "this custom feed"}.`,
        description:
          "Try a different preset or loosen the visibility overrides in the feed manager.",
        buttonText: "Edit Feed",
        onClick: onManageCustomFeed,
      };
    }

    switch (activeSurface.id) {
      case "subscriptions":
        return {
          image: "/assets/empty-30.png",
          title: "Sorry dear, members only!",
          description:
            "To view gated and exclusive content, you'll need to subscribe to creators on Focus.",
          buttonText: "Find Active Users",
          onClick: onOpenDiscover,
        };
      case "following":
        return {
          image: "/assets/empty-32.png",
          title: "Your following feed is empty.",
          description: "Follow more creators to fill this surface with posts.",
          buttonText: "Discover Users",
          onClick: onOpenDiscover,
        };
      case "new":
      case "for-you":
      default:
        return {
          image: "/assets/empty-14.png",
          title: "We couldn't find any posts for you.",
          description: "Try browsing the feed store to find a feed you like.",
          buttonText: "Browse Feed Store",
          onClick: onOpenFeedStore,
        };
    }
  };

  const emptyStateProps = getEmptyStateProps();

  return (
    <div
      className="
        relative top-10 flex items-start justify-center px-12 text-center
        md:px-4
      "
    >
      <EmptyState
        image={emptyStateProps.image}
        title={emptyStateProps.title}
        description={emptyStateProps.description}
        buttonText={emptyStateProps.buttonText}
        onClick={emptyStateProps.onClick}
      />
    </div>
  );
};
